import React, { useState, useMemo, Fragment } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Button from "@mui/material/Button";
import Loader from "components/Progress";
import Table from "components/Table/newTable";
import Chart from "components/Charts/Alarm History";
import { useGetAlarmLogQuery } from "../../../services/alarms";
import {
  timeLabel,
  ruleLabel,
  valueLabel,
  problemLabel,
  notifyLabels,
} from "../logLabels";

// The history inside an opened alarm row (2026-10-30): the raises of that rule
// on that asset at that priority, as dots over the chosen range, with the list
// of raises below. These can include earlier or later alarm records.

// The alarm log keeps 31 days, so the range never starts earlier.
const LOG_DAYS = 31;
const DAY_MS = 24 * 60 * 60 * 1000;
// the range a row's history opens on
const SHOWN_DAYS = 7;
// the chart shows at most this many of the newest raises
const CHART_LIMIT = 1000;

const columns = [
  { id: "time", label: "Time", align: "center" },
  { id: "value", label: "Value", align: "center" },
  { id: "problem", label: "Problem", align: "center" },
  { id: "notify", label: "E-mail", align: "center" },
];

function logStart(now) {
  const earliest = new Date(now);
  earliest.setDate(earliest.getDate() - LOG_DAYS);
  return earliest;
}

const isValidDate = (d) => d instanceof Date && !isNaN(d);

// A value counts as a number only when it is not null or blank and Number()
// reads it as finite (numeric text included). Zone raises store null.
function isNumber(value) {
  return (
    value !== undefined &&
    value !== null &&
    typeof value !== "object" &&
    String(value).trim() !== "" &&
    Number.isFinite(Number(value))
  );
}

// Threshold lines only when every raise has the same threshold: a number is
// one line, a "min-max" range two.
function thresholdLines(entries) {
  const first = entries[0]?.threshold;
  if (first === undefined || first === null) return [];
  const same = entries.every(
    (e) =>
      e.threshold !== undefined &&
      e.threshold !== null &&
      String(e.threshold) === String(first)
  );
  if (!same) return [];
  if (isNumber(first)) return [Number(first)];
  const range = String(first)
    .trim()
    .match(/^(-?\d+(\.\d+)?)-(-?\d+(\.\d+)?)$/);
  return range ? [Number(range[1]), Number(range[3])] : [];
}

export default function AlarmHistory({ alarm, serviceId, width, indent = 0 }) {
  let token = window.localStorage.getItem("token");
  // Set once when the row opens: the last 7 days, as the dashboard's own default
  // range (owner, 2026-10-30; was 24 hours, then 31 days). The pickers reach back
  // the log's 31 days. Requests are built only from this state, so they stay the
  // same between renders.
  const [openedAt] = useState(() => Date.now());
  const [range, setRange] = useState(() => ({
    start: new Date(openedAt - SHOWN_DAYS * DAY_MS),
    end: new Date(openedAt),
  }));
  // the pickers' values while typing; applied on accept, blur or Enter
  const [draft, setDraft] = useState(range);
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);

  const params = `?source=${encodeURIComponent(
    JSON.stringify([alarm.sensorId])
  )}&alarmType=${encodeURIComponent(
    alarm.type
  )}&priority=${encodeURIComponent(
    JSON.stringify([alarm.severity])
  )}&dateFrom=${range.start.toISOString()}&dateTo=${range.end.toISOString()}&withTotalPages=true${
    serviceId ? `&serviceId=${serviceId}` : ""
  }`;
  const chartLog = useGetAlarmLogQuery({
    token,
    params: `${params}&pageSize=${CHART_LIMIT}&currentPage=1`,
  });
  const listLog = useGetAlarmLogQuery({
    token,
    params: `${params}&pageSize=10&currentPage=${page}`,
  });

  // currentData: this request's result only, so a failed load never shows the previous raises
  const entries = chartLog.currentData?.payload?.data;
  const totalDocuments = chartLog.currentData?.payload?.totalDocuments || 0;

  // Memoised so the chart is rebuilt only when the raises change.
  const chart = useMemo(() => {
    const list = entries || [];
    const numeric = list.length > 0 && list.every((e) => isNumber(e.value));
    return {
      numeric,
      thresholds: numeric ? thresholdLines(list) : [],
      data: list.map((e) => {
        const value = valueLabel(e);
        return {
          t: new Date(e.t).getTime(),
          value: e.value,
          label: [timeLabel(e.t), value ? `Value: ${value}` : "", problemLabel(e)]
            .filter((line) => line)
            .join("\n"),
        };
      }),
    };
  }, [entries]);

  const listData = listLog.currentData?.payload;
  const listRows = useMemo(
    () =>
      (listData?.data || []).map((entry) => ({
        id: entry._id,
        time: timeLabel(entry.t),
        value: valueLabel(entry),
        problem: problemLabel(entry),
        notify: notifyLabels[entry.notify] || "",
      })),
    [listData]
  );

  function changeRange(start, end) {
    setMessage("");
    setRange({ start, end });
    setDraft({ start, end });
    setPage(1);
  }

  // Applies only a complete range with the start before the end and within
  // the log's 31 days; otherwise a message shows and the last good range stays.
  function applyRange(start, end) {
    if (!isValidDate(start) || !isValidDate(end)) {
      setMessage("Enter a complete start and end time.");
      return;
    }
    if (start.getTime() >= end.getTime()) {
      setMessage("The start must be before the end.");
      return;
    }
    if (start < logStart(new Date())) {
      setMessage(`The log keeps the last ${LOG_DAYS} days; pick a later start.`);
      return;
    }
    setMessage("");
    if (
      start.getTime() !== range.start.getTime() ||
      end.getTime() !== range.end.getTime()
    ) {
      changeRange(start, end);
    }
  }

  // the 7 days ending one minute after the alarm's last raise
  function showLastRaise() {
    const end = new Date(new Date(alarm.time).getTime() + 60000);
    const start = new Date(
      Math.max(end.getTime() - SHOWN_DAYS * DAY_MS, logStart(new Date()).getTime())
    );
    if (start >= end) {
      setMessage(`The last raise is older than the log's ${LOG_DAYS} days.`);
      return;
    }
    changeRange(start, end);
  }

  const lastRaise = new Date(alarm.time);
  const lastRaiseBefore = isValidDate(lastRaise) && lastRaise < range.start;
  const lastRaiseKept = lastRaiseBefore && lastRaise >= logStart(openedAt);

  const picker = (key, label) => (
    <DateTimePicker
      label={label}
      value={draft[key]}
      format="dd/MM/yyyy hh:mm aa"
      onChange={(value) => setDraft((d) => ({ ...d, [key]: value }))}
      onAccept={(value) =>
        applyRange(
          key === "start" ? value : draft.start,
          key === "end" ? value : draft.end
        )
      }
      slotProps={{
        textField: {
          size: "small",
          // keeps its width but may shrink in a narrow panel (code review
          // 2026-10-30)
          sx: { width: "220px", maxWidth: "100%" },
          onBlur: () => applyRange(draft.start, draft.end),
          onKeyDown: (e) => {
            if (e.key === "Enter") applyRange(draft.start, draft.end);
          },
        },
      }}
    />
  );

  const loader = (
    <span
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: "16px 0px",
      }}
    >
      <Loader />
    </span>
  );

  function errorText(query) {
    return (
      <p style={{ color: "#bf3535", fontSize: "13px" }}>
        {query.error?.data?.message || "Could not load the raises."}
      </p>
    );
  }

  let body;
  if (chartLog.isFetching) {
    body = loader;
  } else if (chartLog.isError) {
    body = errorText(chartLog);
  } else if (!totalDocuments) {
    body = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          height: "100%",
          color: "#555",
          fontSize: "13px",
        }}
      >
        <p>No raises recorded in this period.</p>
        {lastRaiseBefore ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <p>{`Last raise: ${timeLabel(alarm.time)}`}</p>
            {lastRaiseKept ? (
              <Button size="small" variant="outlined" onClick={showLastRaise}>
                Show
              </Button>
            ) : null}
          </div>
        ) : null}
        <p style={{ color: "#9f9f9f", fontSize: "12px" }}>
          Raises are recorded since the alarm log was switched on and kept 31
          days, so Repeated can be higher than the raises shown. Alarms raised
          by devices themselves are not recorded yet.
        </p>
      </div>
    );
  } else {
    body = (
      <Fragment>
        <p style={{ color: "#555", fontSize: "13px" }}>
          {`${totalDocuments.toLocaleString("en-GB")} ${
            totalDocuments == 1 ? "raise" : "raises"
          } in this period`}
          {totalDocuments > CHART_LIMIT ? (
            <span style={{ color: "#9f9f9f" }}>
              {` (the chart is showing the latest ${CHART_LIMIT.toLocaleString(
                "en-GB"
              )})`}
            </span>
          ) : null}
        </p>
        <Chart
          mode="dots"
          data={chart.data}
          height={150}
          numeric={chart.numeric}
          thresholds={chart.thresholds}
          start={range.start.getTime()}
          end={range.end.getTime()}
        />
        {listLog.isFetching ? (
          loader
        ) : listLog.isError ? (
          errorText(listLog)
        ) : (
          <Table
            columns={columns}
            rows={listRows}
            page={page}
            totalPages={listData?.totalPages || 0}
            totalDocuments={listData?.totalDocuments || 0}
            handleChange={setPage}
            pageSize={10}
            size="small"
            minHeight={"auto"}
            paginationMargin={"8px 0px"}
          />
        )}
      </Fragment>
    );
  }

  return (
    // nested under its row: starts right of the row's checkbox (`indent`), bordered,
    // and kept in view when the table scrolls sideways (2026-10-30, owner)
    <div
      style={{
        position: "sticky",
        left: indent,
        marginLeft: indent,
        marginRight: "16px",
        marginBottom: "10px",
        width: width ? width - indent - 16 : `calc(100% - ${indent + 16}px)`,
        height: "360px",
        boxSizing: "border-box",
        padding: "16px",
        backgroundColor: "#fcfcfd",
        border: "1px solid #e3e3e8",
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <p
          style={{
            color: "#bfbec8",
            fontSize: "15px",
            fontWeight: "bold",
          }}
        >
          {`Raises of ${ruleLabel(alarm.type)} on ${alarm.deviceName}, ${
            alarm.severity
          }`}
        </p>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {/* the pickers wrap onto two lines in a narrow panel (code review
              2026-10-30) */}
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: "10px", minWidth: 0 }}
          >
            {picker("start", "Start Time")}
            {picker("end", "End Time")}
          </div>
        </LocalizationProvider>
      </div>
      {message ? (
        <p style={{ color: "#bf3535", fontSize: "12px" }}>{message}</p>
      ) : null}
      {/* the chart and the list scroll inside the panel */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{body}</div>
    </div>
  );
}
